// Whether InLeague is refusing us, and when it was last read, for the bar every
// signed-in page shows and the "InLeague data as of" line some pages show.
//
// Both rules live in the referee Worker's verdict (`inleague_down`,
// `last_read`, apps/referee/src/dao/inleagueHealth.ts), relayed by the
// console at /healthz/inleague with CORS for our app hosts. This only reads
// them.
//
// ⚠️ ANYTHING GOING WRONG ON OUR SIDE MEANS NO BAR. A failed fetch, a CORS
// refusal, a timeout or an odd body all read as not down: telling every coach
// InLeague is down when it is our own check that broke teaches people to
// ignore the bar. Better Stack pages us on those. The same failure keeps the
// last known read times (spec amendment, 2026-10-08): only a successful read
// replaces them, so a dropped poll does not blank the line.
//
// ⚠️ ONE POLL PER PAGE. The bar is in every app shell and the line on some
// pages, so one page can hold two readers. They share one poller per URL
// (`subscribeInleagueHealth`): one interval and one visibility listener,
// started by the first subscriber and stopped by the last (spec 2026-10-08).

import { useEffect, useState } from 'react'
import { isStagingHost, stagingHost, type RefreshStep } from '@region13/shared-types'

export const INLEAGUE_STATUS_PAGE = 'https://status.ayso13.org'
export const INLEAGUE_POLL_MS = 5 * 60_000
const FETCH_TIMEOUT_MS = 10_000

/** One reading of the health route. */
export interface InleagueHealth {
  /** InLeague is refusing us: the body's `inleague_down === true`. */
  readonly down: boolean
  /** When each refresh step last read InLeague (`last_read`), null for a step
   *  with no known read. */
  readonly lastRead: Readonly<Record<RefreshStep, string | null>>
}

/** Before any reading lands: no bar and no line. */
export const NO_INLEAGUE_HEALTH: InleagueHealth = Object.freeze({
  down: false,
  lastRead: Object.freeze({ directory: null, teams: null, players: null, audience: null }),
})

/** A time the line can show: a string that parses as a date, else null. */
const asTime = (v: unknown): string | null => (typeof v === 'string' && !Number.isNaN(Date.parse(v)) ? v : null)

/** The health route for this app's environment, or null off our zone (local
 *  dev, previews), where nothing is fetched. The hostname decides, never a
 *  `VITE_` flag, for the reason StagingBanner gives. */
export function inleagueHealthUrl(hostname: string): string | null {
  const host = hostname.toLowerCase()
  if (!host.endsWith('.ayso13.org')) return null
  return isStagingHost(host)
    ? `https://${stagingHost('admin')}/healthz/inleague`
    : 'https://admin.ayso13.org/healthz/inleague'
}

/** One read. The route answers 503 when down, so the status is not checked:
 *  only the body counts. `down` is `inleague_down === true` exactly. When the
 *  body carries a `last_read` object, each step's time is its string only if it
 *  parses as a date. Anything else (a failed fetch, a body that is not an
 *  object, no `last_read` object) keeps `previous.lastRead`, the times the last
 *  successful read gave. */
export async function readInleagueHealth(
  url: string,
  fetchImpl: typeof fetch = fetch,
  previous: InleagueHealth = NO_INLEAGUE_HEALTH,
): Promise<InleagueHealth> {
  const failed: InleagueHealth = { down: false, lastRead: previous.lastRead }
  try {
    const res = await fetchImpl(url, {
      cache: 'no-store',
      credentials: 'omit',
      signal: AbortSignal.timeout(FETCH_TIMEOUT_MS),
    })
    const body: unknown = await res.json()
    if (typeof body !== 'object' || body === null) return failed
    const { inleague_down: down, last_read: read } = body as { inleague_down?: unknown; last_read?: unknown }
    if (typeof read !== 'object' || read === null) return { ...failed, down: down === true }
    const r = read as Partial<Record<RefreshStep, unknown>>
    return {
      down: down === true,
      lastRead: {
        directory: asTime(r.directory), teams: asTime(r.teams), players: asTime(r.players),
        audience: asTime(r.audience),
      },
    }
  } catch {
    return failed
  }
}

type Listener = (health: InleagueHealth) => void

interface Poller {
  latest: InleagueHealth
  readonly listeners: Set<Listener>
  stop(): void
}

/** One poller per URL, for the whole page. */
const pollers = new Map<string, Poller>()

/** Reads now, every INLEAGUE_POLL_MS while the tab is visible, and again when
 *  it becomes visible, handing each reading to every listener. */
function startPoller(url: string): Poller {
  let stopped = false
  const check = () => {
    if (document.visibilityState !== 'visible') return
    void readInleagueHealth(url, fetch, poller.latest).then((health) => {
      if (stopped) return
      poller.latest = health
      for (const listener of poller.listeners) listener(health)
    })
  }
  const timer = setInterval(check, INLEAGUE_POLL_MS)
  document.addEventListener('visibilitychange', check)
  const poller: Poller = {
    latest: NO_INLEAGUE_HEALTH,
    listeners: new Set(),
    stop() {
      stopped = true
      clearInterval(timer)
      document.removeEventListener('visibilitychange', check)
    },
  }
  check()
  return poller
}

/** Adds a reader of `url`'s health and hands it the latest reading at once
 *  (NO_INLEAGUE_HEALTH before the first one lands). Starts the poll for the
 *  first reader. Returns the unsubscribe; the last reader out stops the poll.
 *  Calling the unsubscribe twice is harmless. */
export function subscribeInleagueHealth(url: string, listener: Listener): () => void {
  let poller = pollers.get(url)
  if (!poller) {
    poller = startPoller(url)
    pollers.set(url, poller)
  }
  const p = poller
  p.listeners.add(listener)
  listener(p.latest)
  return () => {
    p.listeners.delete(listener)
    if (p.listeners.size === 0 && pollers.get(url) === p) {
      p.stop()
      pollers.delete(url)
    }
  }
}

/** The page's shared reading for this app's environment. Off our zone it
 *  never fetches and stays NO_INLEAGUE_HEALTH. */
export function useInleagueHealth(hostname: string): InleagueHealth {
  const [health, setHealth] = useState<InleagueHealth>(NO_INLEAGUE_HEALTH)
  useEffect(() => {
    const url = inleagueHealthUrl(hostname)
    if (!url) return
    return subscribeInleagueHealth(url, setHealth)
  }, [hostname])
  return health
}
